import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import GetAppIcon from '@mui/icons-material/GetApp';
import { addMonths } from 'date-fns';
import PREDEFINED_INTERVALS from '../../../shared/constants/predefinedIntervals';
import ActionBarDateTimePicker from '../../../shared/ui/ActionBarDateTimePicker/ActionBarDateTimePicker';
import { getUtcOffsetMs, getBrowserTimeshift } from '../../../shared/utils/timezones';

function VehicleHistoryIntervalWidget({
  intervalOption,
  setIntervalOption,
  setDisableExternalExtent,
  dateStart,
  setDateStart,
  dateFinish,
  setDateFinish,
  isValidDate,
  getCustomHistory,
  handleExportTracks,
  handleCurrentPosition,
  timeZone,
}) {
  const offsetMs = useMemo(() => getUtcOffsetMs(timeZone), [timeZone]);
  const browserOffsetMs = useMemo(() => getUtcOffsetMs(getBrowserTimeshift()), []);
  const tzDelta = useMemo(() => offsetMs - browserOffsetMs, [offsetMs, browserOffsetMs]);

  const displayDateStart = useMemo(
    () => (dateStart && tzDelta ? new Date(dateStart.getTime() + tzDelta) : dateStart),
    [dateStart, tzDelta],
  );
  const displayDateFinish = useMemo(
    () => (dateFinish && tzDelta ? new Date(dateFinish.getTime() + tzDelta) : dateFinish),
    [dateFinish, tzDelta],
  );
  const displayMaxDateFinish = useMemo(
    () => (displayDateStart ? addMonths(displayDateStart, 1) : null),
    [displayDateStart],
  );

  /**
   * Обрабатывает изменение даты старта.
   * Если новая дата старта валидна, проверяет дату окончания и при превышении
   * ограничивает её значением «дата старта + 1 месяц».
   * @param {Date|null} e - Новое значение даты старта.
   */
  const handleDateStartChange = (e) => {
    const nextDateStart = e && tzDelta ? new Date(e.getTime() - tzDelta) : e;
    setDateStart(nextDateStart);

    if (!isValidDate(nextDateStart)) return;

    const maxDateFinish = addMonths(nextDateStart, 1);
    if (!isValidDate(dateFinish) || dateFinish.getTime() > maxDateFinish.getTime()) {
      setDateFinish(maxDateFinish);
    }
  };
  /**
   * Обрабатывает изменение даты окончания.
   * Если дата старта валидна, невалидное или превышающее «дата старта + 1 месяц»
   * значение приводится к этому лимиту.
   * @param {Date|null} e - Новое значение даты окончания.
   */
  const handleDateFinishChange = (e) => {
    const nextDateFinish = e && tzDelta ? new Date(e.getTime() - tzDelta) : e;

    if (nextDateFinish && isValidDate(dateStart)) {
      const maxDateFinish = addMonths(dateStart, 1);
      if (!isValidDate(nextDateFinish) || nextDateFinish.getTime() > maxDateFinish.getTime()) {
        setDateFinish(maxDateFinish);
        return;
      }
    }

    setDateFinish(nextDateFinish);
  };

  return (
    <Box>
      <FormControl sx={{ marginBottom: '10px' }} fullWidth>
        <InputLabel id="demo-simple-select-label">Интервал</InputLabel>
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={intervalOption}
          label="Интервал"
          onChange={(e) => {
            setDisableExternalExtent(false);
            setIntervalOption(e.target.value);
          }}
        >
          <MenuItem value={PREDEFINED_INTERVALS.TODAY}>Сегодня</MenuItem>
          <MenuItem value={PREDEFINED_INTERVALS.YESTERDAY}>Вчера</MenuItem>
          <MenuItem value={PREDEFINED_INTERVALS.DAY}>Сутки</MenuItem>
          <MenuItem value={PREDEFINED_INTERVALS.WEEK}>Неделя</MenuItem>
          <MenuItem value={PREDEFINED_INTERVALS.CURRENT_MONTH}>Текущий месяц</MenuItem>
          <MenuItem value={PREDEFINED_INTERVALS.LAST_MONTH}>Прошедший месяц</MenuItem>
          <MenuItem value={PREDEFINED_INTERVALS.CUSTOM}>Выбранный период</MenuItem>
        </Select>
      </FormControl>

      {intervalOption === PREDEFINED_INTERVALS.CUSTOM && (
        <Box sx={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <DateTimePicker
            label="C"
            value={displayDateStart}
            onError={() => {}}
            onChange={handleDateStartChange}
            error={false}
            renderInput={(params) => (
              <TextField variant="standard" size="small" fullWidth id={1} {...params} error={!dateStart} />
            )}
            components={{ ActionBar: ActionBarDateTimePicker }}
            validationError={false}
          />
          <DateTimePicker
            label="По"
            value={displayDateFinish}
            minDateTime=""
            maxDateTime={displayMaxDateFinish || undefined}
            onChange={handleDateFinishChange}
            error={false}
            components={{ ActionBar: ActionBarDateTimePicker }}
            validationError={false}
            renderInput={(params) => (
              <TextField variant="standard" size="small" fullWidth id={2} {...params} error={!dateFinish} />
            )}
          />
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          gap: '10px',
          marginBottom: '10px',
          flexWrap: 'wrap',
        }}
      >
        {intervalOption === PREDEFINED_INTERVALS.CUSTOM && (
          <Button
            color={isValidDate(dateStart) && isValidDate(dateFinish) ? 'primary' : 'error'}
            variant="contained"
            onClick={getCustomHistory}
          >
            Показать историю
          </Button>
        )}
        {handleExportTracks && (
          <Button
            title="Выгрузить треки"
            variant="contained"
            disabled={!(isValidDate(dateStart) && isValidDate(dateFinish))}
            onClick={handleExportTracks}
          >
            <GetAppIcon />
          </Button>
        )}

        <Button variant="contained" onClick={handleCurrentPosition}>
          Текущее положение
        </Button>
      </Box>
    </Box>
  );
}

VehicleHistoryIntervalWidget.propTypes = {
  intervalOption: PropTypes.string.isRequired,
  setIntervalOption: PropTypes.func.isRequired,
  setDisableExternalExtent: PropTypes.func.isRequired,
  dateStart: PropTypes.instanceOf(Date),
  setDateStart: PropTypes.func.isRequired,
  dateFinish: PropTypes.instanceOf(Date),
  setDateFinish: PropTypes.func.isRequired,
  isValidDate: PropTypes.func.isRequired,
  getCustomHistory: PropTypes.func.isRequired,
  handleExportTracks: PropTypes.func,
  handleCurrentPosition: PropTypes.func.isRequired,
  timeZone: PropTypes.string,
};

VehicleHistoryIntervalWidget.defaultProps = {
  handleExportTracks: null,
  dateStart: null,
  dateFinish: null,
  timeZone: null,
};

export default VehicleHistoryIntervalWidget;
